import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import AdminService from "../../../../service/AdminService";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import { buildSearchText, formatDay, getToken, normalizeText } from "../../utils/adminUtils";
import StatusTabs from "../../components/list/StatusTabs";
import SearchInput from "../../components/list/SearchInput";
import StatusBadge from "../../components/list/StatusBadge";
import EmptyState from "../../components/list/EmptyState";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";

const NOT_PROCESSED = "NOT PROCESSED";
const TABS = { PENDING: "pending", PROCESSED: "processed", ALL: "all" };

const ORDER_STATUS = {
  PAID: { labelKey: "paid", tone: "warning", icon: "pi pi-clock" },
  SENT: { labelKey: "sent", tone: "neutral", icon: "pi pi-truck" },
  RECEIVED: { labelKey: "received", tone: "good", icon: "pi pi-check-circle" },
};

const loadBangoodOrders = async () =>
  expectCode(await new AdminService().getBangoodRequests(getToken()), 200001).result;

const personLabel = (person) =>
  `${person?.firstname || ""} ${person?.surname || ""}`.trim() || person?.username || "—";

const AdminBangood = () => {
  const history = useHistory();
  const { items, loading, loadError, reload } = useAdminList(loadBangoodOrders);
  const { run, runningId } = useAdminAction();
  const [tab, setTab] = useState(TABS.PENDING);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const rows = useMemo(
    () =>
      items.map((order) => ({
        ...order,
        isPending: order.status_order_banggood === NOT_PROCESSED,
        itemsCount: order.itens?.length || 0,
        dateValue: order.date ? new Date(order.date).getTime() || 0 : 0,
        // A busca do clássico procurava campos de saque (IBAN, banco) que pedidos não têm
        searchText: buildSearchText([
          String(order.id ?? ""),
          order.buyer?.username,
          order.buyer?.firstname,
          order.buyer?.surname,
          order.seller?.username,
          order.seller?.companyName,
        ]),
      })),
    [items],
  );

  const counts = useMemo(
    () => ({
      [TABS.PENDING]: rows.filter((row) => row.isPending).length,
      [TABS.PROCESSED]: rows.filter((row) => !row.isPending).length,
      [TABS.ALL]: rows.length,
    }),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const term = normalizeText(search.trim());
    return rows.filter((row) => {
      if (tab === TABS.PENDING && !row.isPending) return false;
      if (tab === TABS.PROCESSED && row.isPending) return false;
      return !term || row.searchText.includes(term);
    });
  }, [rows, tab, search]);

  const processSelected = async () => {
    const order = selected;
    const ok = await run(
      order.id,
      async () => expectCode(await new AdminService().postProcessBangood(getToken(), order.id), 200000),
      "processOrderSuccessfully",
    );
    setSelected(null);
    if (ok) reload();
  };

  const hasFilters = !!search.trim();

  return (
    <div className="admin-list">
      <ConfirmActionDialog
        visible={!!selected}
        message={localeText("processThisRequest")}
        details={
          selected
            ? [
                { label: localeText("adminOrdersOrder"), value: `#${selected.id}` },
                { label: localeText("byBuyer"), value: personLabel(selected.buyer) },
                { label: localeText("seller"), value: personLabel(selected.seller) },
                { label: localeText("itemsQuantity"), value: String(selected.itemsCount) },
              ]
            : []
        }
        confirmLabel={localeText("process")}
        loading={!!selected && runningId === selected.id}
        onConfirm={processSelected}
        onCancel={() => setSelected(null)}
      />

      <StatusTabs
        tabs={[
          { value: TABS.PENDING, label: localeText("adminBangoodToProcess") },
          { value: TABS.PROCESSED, label: localeText("adminBangoodProcessed") },
          { value: TABS.ALL, label: localeText("adminUsersStatusAll") },
        ]}
        value={tab}
        onChange={setTab}
        counts={counts}
        loading={loading}
      />

      <section className="admin-list__card">
        <div className="admin-list__toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={localeText("adminOrdersSearchPlaceholder")}
          />
          <span className="admin-list__result-count">
            {loading ? "" : `${filteredRows.length} ${localeText("adminUsersResults")}`}
          </span>
        </div>

        <div className="admin-list__table">
          <DataTable
            value={filteredRows}
            dataKey="id"
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate={localeText("showingFirstToLast")}
            sortField="dateValue"
            sortOrder={-1}
            removableSort
            scrollable
            tableStyle={{ minWidth: "56rem" }}
            emptyMessage={
              loadError ? (
                <EmptyState
                  icon="pi pi-exclamation-triangle"
                  message={localeText("adminListLoadError")}
                  actionLabel={localeText("adminUsersRetry")}
                  onAction={reload}
                />
              ) : (
                <EmptyState
                  icon={tab === TABS.PENDING && !hasFilters ? "pi pi-check-circle" : "pi pi-inbox"}
                  message={localeText(
                    hasFilters
                      ? "adminListNoResults"
                      : tab === TABS.PENDING
                        ? "adminBangoodNoPending"
                        : "adminListEmpty",
                  )}
                  actionLabel={hasFilters ? localeText("adminUsersClearFilters") : null}
                  onAction={() => setSearch("")}
                />
              )
            }
          >
            <Column
              header={localeText("adminOrdersOrder")}
              field="id"
              body={(order) => (
                <div className="admin-stack">
                  <span className="admin-strong">#{order.id}</span>
                  <span className="admin-muted">
                    {order.itemsCount} {localeText("adminOrdersItems")}
                  </span>
                </div>
              )}
              sortable
            />
            <Column
              header={localeText("byBuyer")}
              field="buyer.username"
              body={(order) => (
                <div className="admin-stack">
                  <span>{personLabel(order.buyer)}</span>
                  {order.buyer?.username && (
                    <span className="admin-muted">@{order.buyer.username}</span>
                  )}
                </div>
              )}
              sortable
            />
            <Column
              header={localeText("seller")}
              field="seller.username"
              body={(order) => (
                <div className="admin-stack">
                  <span>{order.seller?.companyName || personLabel(order.seller)}</span>
                  {order.seller?.username && (
                    <span className="admin-muted">@{order.seller.username}</span>
                  )}
                </div>
              )}
              sortable
            />
            <Column
              header={localeText("status")}
              field="status"
              body={(order) => {
                const config = ORDER_STATUS[order.status];
                return config ? (
                  <StatusBadge tone={config.tone} icon={config.icon}>
                    {localeText(config.labelKey)}
                  </StatusBadge>
                ) : (
                  <StatusBadge tone="neutral">{order.status || "—"}</StatusBadge>
                );
              }}
              sortable
            />
            <Column
              header="Bangood"
              field="isPending"
              body={(order) =>
                order.isPending ? (
                  <StatusBadge tone="warning">{localeText("adminBangoodToProcess")}</StatusBadge>
                ) : (
                  <StatusBadge tone="good">
                    {order.status_order_banggood || localeText("adminBangoodProcessed")}
                  </StatusBadge>
                )
              }
              sortable
            />
            <Column
              header={localeText("date")}
              field="dateValue"
              body={(order) => formatDay(order.date)}
              sortable
            />
            <Column
              header={localeText("actions")}
              frozen
              alignFrozen="right"
              headerClassName="admin-list__actions-cell"
              bodyClassName="admin-list__actions-cell"
              body={(order) => (
                <div className="admin-list__header-actions">
                  <button
                    type="button"
                    className="admin-icon-btn"
                    onClick={() => history.push(`/dashboard-purchasesdetails?orderId=${order.id}`)}
                    title={localeText("orderDetails")}
                    aria-label={localeText("orderDetails")}
                  >
                    <i className="pi pi-eye" aria-hidden="true" />
                  </button>
                  {order.isPending && (
                    <button
                      type="button"
                      className="admin-btn admin-btn--primary"
                      onClick={() => setSelected(order)}
                      disabled={runningId === order.id}
                    >
                      <i
                        className={runningId === order.id ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        aria-hidden="true"
                      />
                      {localeText("process")}
                    </button>
                  )}
                </div>
              )}
            />
          </DataTable>
        </div>
      </section>
    </div>
  );
};

export default AdminBangood;
